/**
 * T-143-002 — Calendar Booking Link & Prep Email Automation
 *
 * Feature: F-185 / E-032 / US-143
 *
 * BE HANDOFF CONTRACT:
 * 1. Booking Link: Configurable Google Calendar consultation URL resolved via
 *    `getMeetingLinkUrl({ tier, campaign, abVariant })`.
 *    Configured default: `https://calendar.app.google/EVFGmvGSuHpv2YdX9`.
 *    Appends statutory UTM routing parameters (`plan_type`, `utm_campaign`, `ab_variant`).
 * 2. Prep Email Trigger: Handled by backend endpoint `POST api/meeting-link/confirm`
 *    (mounted in `landlord-pro/src/routes/meeting-link.js` before catch-all).
 * 3. Asynchronous Execution: On CTA click, opens calendar booking in new tab and triggers
 *    `POST api/meeting-link/confirm` fire-and-receive. The backend sends the Brevo transactional
 *    meeting confirmation / preparation email (`sendMeetingConfirmation` via Brevo template)
 *    and responds with `{ success: true, data: { meetingUrl, planType, emailStatus } }`.
 * 4. Fail-Soft: Even if the mailer or network call encounters transient failures,
 *    the calendar link navigation succeeds, ensuring zero conversion loss.
 */

import React, { useState } from 'react';
import { getMeetingLinkUrl } from '../../config/meeting-link';
import { landlordApi } from '../../services/api';

export interface CalendarBookingSectionProps {
  tier?: string;
  campaign?: string;
  abVariant?: string;
  title?: string;
  description?: string;
  ctaText?: string;
  className?: string;
  onBookingInitiated?: (bookingUrl: string) => void;
}

export const CalendarBookingSection: React.FC<CalendarBookingSectionProps> = ({
  tier = 'enterprise',
  campaign = 'enterprise-sso-lead',
  abVariant,
  title = 'Schedule Your Enterprise Architecture Review',
  description = 'Book a 1-on-1 consultation with our Texas compliance systems architect. We’ll review your IdP requirements, multi-city compliance mapping, and custom SSO provisioning.',
  ctaText = 'Book Calendar Consultation',
  className = '',
  onBookingInitiated,
}) => {
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string | null>(null);
  const [confirmedUrl, setConfirmedUrl] = useState<string | null>(null);

  const bookingUrl = getMeetingLinkUrl({ tier, campaign, abVariant });

  const handleBookingClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Note: link opens in new tab via target="_blank"
    setLoading(true);
    setEmailStatus('QUEUED');

    try {
      // Backend confirmation endpoint: POST api/meeting-link/confirm
      const response = await landlordApi.post('api/meeting-link/confirm', {
        tier: tier || null,
        campaign: campaign || null,
        abVariant: abVariant || null,
      });

      const data = response?.data?.data || response?.data;
      if (data?.emailStatus) {
        setEmailStatus(data.emailStatus);
      } else {
        setEmailStatus('SENT');
      }
      setConfirmedUrl(data?.meetingUrl || bookingUrl);
    } catch (err: any) {
      console.warn('[CalendarBooking] Prep email confirmation non-fatal warning:', err?.message);
      setEmailStatus('SENT_FALLBACK');
    } finally {
      setLoading(false);
      if (onBookingInitiated) {
        onBookingInitiated(bookingUrl);
      }
    }
  };

  return (
    <div
      data-testid="calendar-booking-section"
      className={`bg-slate-900 text-white rounded-xl p-6 border border-slate-800 shadow-lg ${className}`}
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-900/60 border border-blue-700/60 text-blue-300 text-xs font-semibold mb-3">
            <i className="fas fa-calendar-alt"></i>
            <span>Instant Calendar Integration</span>
          </div>
          <h3 className="text-xl font-bold text-white tracking-tight">{title}</h3>
          <p className="text-slate-300 text-sm mt-2 leading-relaxed">{description}</p>

          {/* Automated Prep Email Status Indicator */}
          {emailStatus && (
            <div
              data-testid="prep-email-status"
              className="mt-3 flex items-center gap-2 text-xs text-emerald-400 font-medium"
            >
              <i className="fas fa-envelope-check"></i>
              <span>
                {emailStatus === 'QUEUED' && 'Dispatching preparation agenda to your email...'}
                {emailStatus === 'SENT' && 'Preparation agenda dispatched to your email via Brevo.'}
                {emailStatus === 'SENT_FALLBACK' && 'Calendar reservation active — calendar invite on confirmation.'}
                {emailStatus !== 'QUEUED' && emailStatus !== 'SENT' && emailStatus !== 'SENT_FALLBACK' && `Mailer status: ${emailStatus}`}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3">
          <a
            data-testid="booking-cta-btn"
            href={confirmedUrl || bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleBookingClick}
            className="w-full sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-bold text-sm rounded-lg shadow-lg shadow-blue-900/30 transition flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>Preparing Booking...</span>
              </>
            ) : (
              <>
                <i className="fas fa-calendar-check"></i>
                <span>{ctaText}</span>
              </>
            )}
          </a>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-800 flex flex-wrap items-center justify-between text-xs text-slate-400 gap-2">
        <span className="flex items-center gap-1.5">
          <i className="fas fa-lock text-slate-500"></i>
          <span>Direct Google Calendar Link:</span>
          <span data-testid="calendar-link" className="text-slate-300 font-mono">
            {bookingUrl}
          </span>
        </span>
        <span className="text-slate-500">Includes plan_type &amp; attribution tags</span>
      </div>
    </div>
  );
};

export default CalendarBookingSection;
